import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import App from "./app/App.tsx";
import { LandingPage } from "./app/pages/LandingPage.tsx";
import { WebPass } from "./app/pages/WebPass.tsx";
import { SignupPage } from "./app/pages/SignupPage.tsx";
import { OnboardingWizard } from "./app/pages/OnboardingWizard.tsx";
import { LegalPage } from "./app/pages/LegalPage.tsx";
import { NotFoundPage } from "./app/pages/NotFoundPage.tsx";
import "./styles/index.css";
import { supabase } from "./app/lib/woocommerce.ts";
import { BrandProvider, fetchWhiteLabelConfig } from "./app/store/BrandContext.tsx";
import { PwaInstallPrompt } from "./app/components/PwaInstallPrompt";
import { StoreProvider } from './app/context/StoreContext';
import { buildStoreIconEntries, storeManifestUrl } from './app/lib/pwaManifest';
import { getStoreSubdomainSlug, isMasterHost } from './app/lib/storeDomain';
import { serializeError } from './app/lib/serializeError';
import { ErrorBoundary } from './app/components/ErrorBoundary';

const SlideShow = lazy(() => import('./app/components/SlideShow').then(m => ({ default: m.SlideShow })));

// Supabase errors (FunctionsHttpError, AuthError, PostgrestError) carry
// non-enumerable props and console.error(err) prints them as `{}`.
// Patch console.error globally so every call site logs the real message.
const _consoleError = console.error.bind(console);
console.error = (...args: any[]) =>
  _consoleError(...args.map((a) => {
    if (a instanceof Error) return serializeError(a);
    if (a && typeof a === 'object' && !(a instanceof Date)) {
      const s = serializeError(a);
      return s === '{}' || s === '[object Object]' ? a : s;
    }
    return a;
  }));

/**
 * RouterRoot — decides what to render based on the current URL.
 */
function RouterRoot() {
  const { pathname } = useLocation();

  // Root path → public landing page — EXCEPT on store custom domains, where
  // "/" is the store's own front door (login/app), not the platform landing.
  if (pathname === "/") {
    const hostname = window.location.hostname;
    const isCustomDomain = !isMasterHost(hostname);
    if (isCustomDomain) {
      return (
        <>
          <App />
          <PwaInstallPrompt />
        </>
      );
    }

    const isPWA = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    // If in PWA mode with a saved store slug, show the main app (which will handle white-label login)
    if (isPWA) {
      return (
        <>
          <App />
          <PwaInstallPrompt />
        </>
      );
    }
    // Install prompt is only offered on store pages and the master /login —
    // installing from the landing root would save "/" as the PWA URL and
    // open the landing page instead of a store's login.
    return <LandingPage />;
  }

  // Login route: all paths to /login just render the main App (which has login screen)
  if (pathname === "/login") {
    return (
      <>
        <App />
        <PwaInstallPrompt />
      </>
    );
  }

  // Self-service signup + guided onboarding (LojaPilot)
  if (pathname === "/signup") {
    return <SignupPage />;
  }
  if (pathname === "/onboarding") {
    return <OnboardingWizard />;
  }

  // Legal pages
  if (pathname === "/privacidade") {
    return <LegalPage kind="privacidade" />;
  }
  if (pathname === "/cookies") {
    return <LegalPage kind="cookies" />;
  }

  // Web Pass route
  if (pathname.startsWith("/wallet/")) {
    const userId = pathname.split("/wallet/")[1];
    return <WebPass userId={userId} />;
  }

  // Public product slide page for digital signage / web embeds —
  // /slide/{slug}?token=&interval=&refresh=. Standalone: no app shell,
  // no auth, just the looping catalog slideshow (works on any app host
  // including store custom domains).
  if (pathname.startsWith("/slide/")) {
    const slug = pathname.split("/slide/")[1]?.split("/")[0] || "";
    if (!slug) return <NotFoundPage />;
    return (
      <Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0a0a0c" }} />}>
        <SlideShow slug={slug} />
      </Suspense>
    );
  }

  // Reserved namespaces: paths under them that matched no real route above
  // are genuine 404s. Bare /{slug} paths still reach the app — they may be
  // storefronts — as does /loja/{slug}.
  const firstSegment = pathname.split("/")[1]?.toLowerCase() || "";
  if (['login', 'signup', 'onboarding', 'privacidade', 'cookies', 'wallet', 'slide'].includes(firstSegment)) {
    return <NotFoundPage />;
  }
  if (firstSegment === 'loja' && !pathname.split("/")[2]) {
    return <NotFoundPage />;
  }

  // All other paths → the main application
  return (
    <>
      <App />
      <PwaInstallPrompt />
    </>
  );
}

// Funcao de injeção radical de icones para PWA
const updatePwaAssets = (config: any, slug: string) => {
  requestAnimationFrame(async () => {
    const ts = new Date().getTime(); // versioning timestamp
    const iconSrc = config.apple_touch_icon_url || config.logo_url;

    // 1. Matar tags antigas de ícone para forçar o iOS a largar o cache.
    // O <link rel="manifest"> é atualizado apenas depois de termos os
    // dados novos prontos (ver passo 3), para nunca deixar a página sem
    // manifest enquanto o fetch está em curso.
    document.querySelectorAll('link[rel="apple-touch-icon"], link[rel="icon"]').forEach(el => el.remove());

    // Always leave a valid icon in the document — stores without a custom
    // logo were previously left with no apple-touch-icon/favicon at all.
    const resolvedIconSrc = iconSrc || '/apple-touch-icon.png';
    const iconUrl = encodeURI(resolvedIconSrc) + (iconSrc ? `?v=${ts}` : '');

    const appleIcon = document.createElement('link');
    appleIcon.setAttribute('rel', 'apple-touch-icon');
    appleIcon.setAttribute('href', iconUrl);
    document.head.appendChild(appleIcon);

    const favicon = document.createElement('link');
    favicon.setAttribute('rel', 'icon');
    favicon.setAttribute('href', iconSrc ? iconUrl : '/favicon.png');
    document.head.appendChild(favicon);

    // 3. Atualizar o Manifest dinâmico com versioning e override de start_url.
    // Construído localmente a partir dos dados de white-label já obtidos em
    // bootstrap() (sem um segundo pedido de rede ao servidor), para nunca
    // haver uma corrida entre "Adicionar ao Ecrã Principal" e a chegada da
    // resposta do endpoint /manifest — se esse pedido fosse lento, bloqueado
    // ou falhasse, o browser podia instalar a PWA com o nome/ícone/URL
    // genéricos em vez dos da loja, impedindo o cliente de voltar a entrar.
    const hostname = window.location.hostname;
    const isCustomDomain = !isMasterHost(hostname);
    const origin = window.location.origin;
    const relativeStartUrl = isCustomDomain ? '/' : (slug && slug !== 'default' ? `/${slug}` : '/');

    // Real https manifest served by the edge function — blob: manifests race
    // with iOS "Add to Home Screen" (it can read the stale static manifest
    // and install the app with the platform name/start_url instead of the
    // store's). Fall back to a local blob if the edge URL can't be built.
    const edgeManifest = storeManifestUrl(slug, isCustomDomain);
    if (edgeManifest) {
      let manifestLink = document.getElementById('pwa-manifest') as HTMLLinkElement | null;
      if (!manifestLink) {
        manifestLink = document.createElement('link');
        manifestLink.setAttribute('id', 'pwa-manifest');
        document.head.appendChild(manifestLink);
      }
      manifestLink.setAttribute('rel', 'manifest');
      manifestLink.setAttribute('href', edgeManifest);
    } else {
    const manifestIconUrl = iconSrc ? `${encodeURI(iconSrc)}?v=${ts}` : `${origin}/brand/lojapilot-app-icon.png`;
    // The edge function serves a guaranteed square PNG at the declared
    // size — Chrome discards non-square/wrong-size icons (and can't fetch
    // blob: URLs from a manifest) and would fall back to the LojaPilot
    // defaults in the install dialog.
    const storeIconEntries = iconSrc
      ? await buildStoreIconEntries(slug, manifestIconUrl)
      : [{ src: manifestIconUrl, sizes: "512x512", type: "image/png", purpose: "any" }];

    // Use absolute URLs for start_url/scope: manifests served via a Blob
    // URL have no reliable base path, so relative URLs can fail to
    // resolve to the correct origin when the app is installed as a PWA.
    const manifestData = {
      id: `${origin}${relativeStartUrl}`,
      name: config.name || "LojaPilot",
      short_name: config.name || "LojaPilot",
      description: "Cria, importa e gere a tua loja online com o LojaPilot.",
      start_url: `${origin}${relativeStartUrl}`,
      scope: `${origin}/`,
      display: "standalone",
      background_color: config.theme_mode === 'light' ? "#ffffff" : "#09090b",
      theme_color: config.brand_color || "#ffffff",
      orientation: "portrait-primary",
      icons: [
        // Fallbacks FIRST: for equal `sizes` Chrome keeps the LAST matching
        // icon, so the store entries below must come after these defaults
        // to win the same-size competition in the install dialog.
        { src: `${origin}/pwa-icon-192.png`, sizes: "192x192", type: "image/png", purpose: "any" },
        { src: `${origin}/pwa-icon-512.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
        ...storeIconEntries]
    };

    const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/manifest+json' });
    const objectUrl = URL.createObjectURL(blob);

    // Reuse the same id used by the static <link id="pwa-manifest"> tag so
    // later brand/context updates can keep targeting this element instead
    // of silently no-oping once it has been replaced here. Update the
    // existing element in place (or create it if missing) instead of
    // removing it upfront, so the page is never briefly left without a
    // manifest link.
    let manifest = document.getElementById('pwa-manifest') as HTMLLinkElement | null;
    if (!manifest) {
      manifest = document.createElement('link');
      manifest.setAttribute('id', 'pwa-manifest');
      document.head.appendChild(manifest);
    }
    manifest.setAttribute('rel', 'manifest');
    manifest.setAttribute('href', objectUrl);
    }

    // 4. Sincronização Segura do Título (document.title + meta usada pelo iOS
    // para o nome mostrado no ecrã principal quando se faz "Adicionar ao
    // Ecrã Principal")
    if (config.name) {
      document.title = config.name;

      let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null;
      if (!appleTitle) {
        appleTitle = document.createElement('meta');
        appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
        document.head.appendChild(appleTitle);
      }
      appleTitle.setAttribute('content', config.name);
    }
  });
};

import { PluginApiClient } from "./app/lib/pluginApiClient.ts";

// Anti-Flicker Bootstrap for Dynamic White Label
async function bootstrap() {
  let slug = '';
  let matchedByDomain = false;
  const hostname = window.location.hostname;
  const pathParts = window.location.pathname.split('/').filter(Boolean);

  // 1. Carregar lista de stores (global_multistore)
  let allStores: any[] = [];
  try {
    const cached = sessionStorage.getItem('global_multistore');
    if (cached) {
      allStores = JSON.parse(cached);
    } else {
      const { data } = await supabase
        .from('swift_kv_store_75caf0c1')
        .select('value')
        .eq('key', 'kv:global:global_multistore')
        .maybeSingle();

      if (data?.value) {
        allStores = data.value;
        sessionStorage.setItem('global_multistore', JSON.stringify(allStores));
      }
    }
  } catch (err) {
    console.warn('[Bootstrap] Failed to load global_multistore:', err);
  }

  // 2. Deteção por domínio (prioridade)
  if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
    // 2a. Store subdomain: {slug}.lojapilot.pt — the subdomain IS the
    // store slug, a per-store origin for isolated PWA install/storage.
    const subdomainSlug = getStoreSubdomainSlug(hostname);
    if (subdomainSlug) {
      matchedByDomain = true;
      slug = subdomainSlug;
      console.log('[Bootstrap] Matched store subdomain:', hostname, '-> slug:', slug);
    } else {
    const matched = allStores.find(
      (s: any) => s.config?.customDomain?.toLowerCase() === hostname.toLowerCase()
    );

    if (matched) {
      matchedByDomain = true;
      slug = matched.config?.customUrl || matched.id;
      console.log('[Bootstrap] Matched custom domain:', hostname, '-> slug:', slug);

      // Se for custom domain, não usar o hostname.split('.')[0] abaixo
      // e nem o path-based fallback
    }
    }
  }

  // 3. Fallback: DOMínio não é custom domain → tentar path-based
  if (!matchedByDomain) {
    if (hostname !== 'localhost' && hostname !== '127.0.0.1' &&
        !hostname.includes('vercel.app') && !hostname.includes('supabase.co') &&
        hostname !== 'woo.goldencreative.pt' && !hostname.endsWith('lojapilot.pt')) {
      // Para domínios que não são master nem localhost, usar o hostname como slug
      // mas apenas se não tivermos custom domain (já coberto acima)
      slug = hostname.split('.')[0].toLowerCase();
    } else {
      if (pathParts[0]?.toLowerCase() === 'loja' && pathParts[1]) {
        // /loja/{slug} — public storefront route
        slug = pathParts[1].toLowerCase();
      } else if (pathParts[0] && !['login', 'wallet', 'signup', 'onboarding'].includes(pathParts[0])) {
        slug = pathParts[0].toLowerCase();
      }
    }
  }

  // 4. Query param fallback
  if (!slug) {
    const urlParams = new URLSearchParams(window.location.search);
    const storeParam = urlParams.get('store') || urlParams.get('s');
    if (storeParam) slug = storeParam.toLowerCase();
  }

  // An installed PWA opens at its manifest start URL and may not retain the
  // store path, so restore the saved store context before loading fallback
  // master branding.
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  if (!slug && isPWA) {
    slug = localStorage.getItem('pwa_store_slug') || '';
  }

  // Expose the resolved slug so later slug-extraction logic (BrandContext,
  // PWA slug vault) uses the real store slug instead of naive parsing —
  // on custom domains hostname.split('.')[0] would wrongly yield "app".
  if (slug) {
    (window as any).__resolvedSlug = slug;
    localStorage.setItem('pwa_store_slug', slug);
  }

  if (slug) {
    // iOS reads apple-mobile-web-app-title (and the manifest name) at
    // "Add to Home Screen" time — set the slug immediately so a store PWA
    // never briefly advertises the platform name while config loads.
    const earlyTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null;
    if (earlyTitle) earlyTitle.setAttribute('content', slug);

    try {
      const cacheKey = `white_label_${slug}`;
      const cached = sessionStorage.getItem(cacheKey);
      let data = null;

      if (cached) {
        data = JSON.parse(cached);
      } else {
        try {
          const dbData = await fetchWhiteLabelConfig(slug);
          if (dbData) {
            data = dbData;
            sessionStorage.setItem(cacheKey, JSON.stringify(data));
          }
        } catch (dbErr) {
          console.warn("Database fetch failed:", dbErr);
        }
      }

      if (data) {
        // Inject Dynamic CSS Variables
        const root = document.documentElement;
        if (data.brand_color) {
          root.style.setProperty('--brand', data.brand_color);
          root.style.setProperty('--brand-color', data.brand_color);
        }

        if (data.theme_mode === 'light') {
          root.classList.add('light');
          root.classList.remove('dark');
          root.style.colorScheme = 'light';
        } else {
          root.classList.add('dark');
          root.classList.remove('light');
          root.style.colorScheme = 'dark';
        }

        if (data.logo_url) {
          data.logo_url = encodeURI(data.logo_url);
        }

        // Executar injeção PWA total protegida com rAF
        updatePwaAssets(data, slug);

        (window as any).__brandConfig = data;
      }
    } catch (err) {
      console.warn("White label loading error:", err);
    }
  } else {
    // No slug in the URL — but if the visitor was on a store before, the
    // remembered store must own the PWA install: point the manifest/touch
    // icon at it so iOS "Add to Home Screen" saves the store URL/name/icon
    // instead of a LojaPilot app opening the landing page.
    const rememberedSlug = (() => { try { return localStorage.getItem('pwa_store_slug') || ''; } catch { return ''; } })();
    if (rememberedSlug) {
      try {
        const rCache = sessionStorage.getItem(`white_label_${rememberedSlug}`);
        const rData = rCache ? JSON.parse(rCache) : await fetchWhiteLabelConfig(rememberedSlug);
        if (rData) {
          if (!rCache) sessionStorage.setItem(`white_label_${rememberedSlug}`, JSON.stringify(rData));
          const sbUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/+$/, '');
          document.querySelectorAll('link[rel="apple-touch-icon"]').forEach(el => el.remove());
          const appleIcon = document.createElement('link');
          appleIcon.setAttribute('rel', 'apple-touch-icon');
          appleIcon.setAttribute('href', sbUrl ? `${sbUrl}/functions/v1/make-server-75caf0c1/icon/${encodeURIComponent(rememberedSlug)}/180` : (rData.apple_touch_icon_url || rData.logo_url || '/apple-touch-icon.png'));
          document.head.appendChild(appleIcon);
          const mUrl = storeManifestUrl(rememberedSlug, false);
          const mLink = document.getElementById('pwa-manifest') as HTMLLinkElement | null;
          if (mLink && mUrl) mLink.href = mUrl;
          if (rData.name) {
            const t = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement | null;
            if (t) t.setAttribute('content', rData.name);
          }
        }
      } catch (e) { console.warn('[PWA] remembered-store branding failed:', e); }
    }

    // No white-label slug detected → load master PWA icon for root /login
    // (skipped when a remembered store already owns the install branding)
    if (!rememberedSlug) try {
      const { data } = await supabase
        .from('global_settings')
        .select('value')
        .eq('key', 'pwa_icon_login')
        .maybeSingle();
      if (data?.value) {
        const ts = Date.now();
        const iconUrl = encodeURI(data.value) + `?v=${ts}`;
        document.querySelectorAll('link[rel="apple-touch-icon"], link[rel="icon"]').forEach(el => el.remove());
        const appleIcon = document.createElement('link');
        appleIcon.setAttribute('rel', 'apple-touch-icon');
        appleIcon.setAttribute('href', iconUrl);
        document.head.appendChild(appleIcon);
        const favicon = document.createElement('link');
        favicon.setAttribute('rel', 'icon');
        favicon.setAttribute('href', iconUrl);
        document.head.appendChild(favicon);
        console.log('[PWA] Master login icon injected:', iconUrl);
      }
    } catch (err) {
      console.warn('[PWA] Could not load master icon:', err);
    }

    // On store domains (custom domain or store subdomain) "/" already IS the
    // store root — rewriting to /{slug} only uglifies the installed PWA URL.
    if (isPWA && slug && !pathParts.length && !matchedByDomain) {
      window.history.replaceState({}, '', `/${slug}`);
    }
  }

  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
      <BrandProvider>
        <BrowserRouter>
          <RouterRoot />
        </BrowserRouter>
      </BrandProvider>
    </ErrorBoundary>
  );
}

// Exposed so BrandContext can await the resolved slug/brand config instead of
// racing against it on custom domains (where slug extraction is meaningless).
(window as any).__bootstrapPromise = bootstrap();